import { Injectable } from '@angular/core';
import { BaseApiService } from '@ep/shared';
import { catchError, of } from 'rxjs';
import { OrderPaymentStatusTypes } from '@shared/enums';
import { OrderReview, OrderReviewsResponse } from '@app/resources/services/order-review.service';
import { Employee } from '@app/resources/services/employee.service';
import { Consumer } from '@app/resources/services/consumer.service';

export interface OrderResponse {
  orderId: number;
  orderGuid: string;
  employeeId: number;
  statusType: number;
  enrollmentType: number;
  amountTotal: number;
  startDateTime: Date | string;
  updateDateTime: Date | string;
  reviewIds: {
    $values: string[];
  };
  orderPayments: OrderPaymentsResponse;
  orderReviews: {
    $id: number;
    $values: OrderReviews[];
  };
  originSDK?: boolean;
}

export interface OrderReviews {
  id: number;
  orderReviewId: string;
  orderId: number;
  consumerId: number;
  score: number;
  totalAsked: number;
  totalAnswered: number;
  createdDateTime: string;
  updateDateTime: string;
  ts: string;
}

export interface OrderPaymentResponse {
  orderPaymentId: number;
  orderPaymentGuid: string;
  statusType: OrderPaymentStatusTypes;
  amountTotal: number;
  amountGratuity: number;
  amountTax: number;
  amountTendered: number;
  amountDiscount: number;
  reward: number;
  rewardRedeemed: number;
  points: number;
  pointsRedeemed: number;
  startDateTime: Date | string;
  orderId: number;
  consumerId: number | null;
  referenceIdent?: string;
}

export interface OrderPaymentsResponse {
  $values: OrderPaymentResponse[];
}

export interface OrdersResponse {
  orders: { $values: OrderResponse[] };
  totalTransactions: number;
}

export interface Order {
  OrderId: number;
  OrderGuid: string;
  EmployeeId: number;
  Employee?: Employee | null;
  StatusType: number;
  EnrollmentType: number;
  AmountTotal: number;
  StartDateTime: Date | string;
  UpdateDateTime: Date | string;
  ReviewIds: string[];
  Reviews: OrderReview[];
  OrderPayments: OrderPayment[];
  OriginSDK?: boolean;
}

export interface OrderPayment {
  OrderPaymentId: number;
  OrderPaymentGuid: string;
  StatusType: OrderPaymentStatusTypes;
  AmountTotal: number;
  AmountGratuity: number;
  AmountTax: number;
  AmountTendered: number;
  AmountDiscount: number;
  Reward: number;
  RewardRedeemed: number;
  Points: number;
  PointsRedeemed: number;
  StartDateTime: Date | string;
  OrderId: number;
  Order?: Order | null;
  ConsumerId: number | null;
  Consumer?: Consumer | null;
  ReferenceIdent?: string;
}

@Injectable({
  providedIn: 'root',
})
export class OrderService extends BaseApiService {
  private readonly orderEndpoint = '/v2/Orders';

  findAll(pageNumber: number, pageSize: number) {
    return this.get<OrdersResponse>(`${this.orderEndpoint}`, { pageNumber: pageNumber, pageSize: pageSize }).pipe(
      catchError((err) => {
        console.error(err);
        const emptyOrdersResponse: OrdersResponse = { orders: { $values: [] }, totalTransactions: 0 };
        return of(emptyOrdersResponse);
      })
    );
  }

  findUnique(orderId: number) {
    return this.get<OrderResponse>(`${this.orderEndpoint}/${orderId}`);
  }

  findAllWithReviews(pageNumber: number, pageSize: number, consumerId: number) {
    return this.get<OrdersResponse>(`${this.orderEndpoint}/GetFullOrdersReviewsByConsumerId`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      consumerId: consumerId,
    }).pipe(
      catchError((err) => {
        console.error(err);
        const emptyOrdersResponse: OrdersResponse = { orders: { $values: [] }, totalTransactions: 0 };
        return of(emptyOrdersResponse);
      })
    );
  }

  findByOrderGuid(pageNumber: number, pageSize: number, query: string, abortSignal?: AbortSignal) {
    const options: { [key: string]: string | number } = { pageNumber, pageSize, query };
    if (abortSignal) {
      (options as any).signal = abortSignal;
    }
    return this.get<OrdersResponse>(`${this.orderEndpoint}/search`, options).pipe(
      catchError((err) => {
        if (err.name === 'AbortError' || err.status === 0) {
          console.info('Search request aborted');
        } else {
          console.error(err);
        }
        const emptyOrdersResponse: OrdersResponse = { orders: { $values: [] }, totalTransactions: 0 };
        return of(emptyOrdersResponse);
      })
    );
  }
}
